/*
 * PROFIT-ROUNDING.JS - Profit Margin Adjustment Calculation Utilities
 *
 * SUMMARY:
 * Utility functions for calculating profit margin adjustments with rounding capabilities.
 * Supports three adjustment methods: FINAL_AMOUNT (with rounding), EXACT_AMOUNT (no rounding), and MARGIN_PERCENT (with rounding).
 *
 * FUNCTIONS:
 * - calculateProfitAdjustment: Main calculation function
 * - roundToIncrement: Round amount to specified increment
 * - calculateMarginFromAmount: Calculate margin from target amount
 * - calculateAmountFromMargin: Calculate amount from target margin
 * - validateInputs: Input validation and business rule checks
 */

import { toUSD } from "../currency-formatter";
import { toDecimal, roundValue } from "../decimal-utils";

/**
 * Rounds a number to the nearest specified increment
 * @param {number} amount - Amount to round
 * @param {number} increment - Rounding increment (e.g., 1000, 10000)
 * @param {string} direction - 'UP' or 'DOWN'
 * @returns {number} Rounded amount
 */
export const roundToIncrement = (amount, increment, direction) => {
  const amountDec = toDecimal(amount);
  const incrementDec = toDecimal(increment || 0);

  // Guard against zero / invalid increment
  if (incrementDec.eq(0)) {
    return roundValue(amountDec, 2);
  }

  const quotient = amountDec.div(incrementDec);
  const roundedQuotient =
    direction === "UP" ? quotient.ceil() : quotient.floor();
  const result = roundedQuotient.times(incrementDec);

  // Return as plain number for external consumers
  const roundedResult = roundValue(result, 2);

  return roundedResult;
};

/**
 * Calculates profit margin percentage from cost and sale amount
 * @param {number} totalCost - Total cost
 * @param {number} saleAmount - Sale amount
 * @returns {number} Profit margin percentage
 */
export const calculateMarginFromAmount = (totalCost, saleAmount) => {
  const costDec = toDecimal(totalCost);
  if (costDec.lte(0)) return 0;

  const saleDec = toDecimal(saleAmount);

  // Formula: |(totalCost - saleAmount) / saleAmount * 100|
  const marginDec = costDec.minus(saleDec).div(saleDec).times(100).abs(); // percentage

  // Return high-precision number; callers can format/round for display
  const margin = roundValue(marginDec, 2);

  return margin;
};

/**
 * Calculates sale amount from cost and profit margin
 * @param {number} totalCost - Total cost
 * @param {number} marginPercent - Profit margin percentage
 * @returns {number} Sale amount
 */
export const calculateAmountFromMargin = (totalCost, marginPercent) => {
  const costDec = toDecimal(totalCost);
  const marginDec = toDecimal(marginPercent);

  // totalCost * (1 + margin/100)
  // const factor = toDecimal(1).plus(marginDec.div(100));
  // const result = costDec.times(factor);

  // const amount = roundValue(result, 2);

  // return amount;
  // Guard: margin must be < 100%
  const one = toDecimal(1);
  const marginFraction = marginDec.div(100);
  
  if (marginFraction.gte(one)) {
    throw new Error('Target margin cannot be 100% or more');
  }

  // True profit margin formula:
  // margin = (R - C) / R  =>  R = C / (1 - margin)
  const denominator = one.minus(marginFraction);
  const result = costDec.div(denominator);

  const amount = roundValue(result, 2);
  return amount;
};

/**
 * Validates input parameters and returns warnings
 * @param {Object} params - Input parameters
 * @returns {Object} Validation result with warnings array
 */
export const validateInputs = ({
  totalCost,
  targetValue,
  adjustmentMethod,
  tenderAmount,
}) => {
  const warnings = [];

  // Validate target amount is greater than total cost for EXACT_AMOUNT
  if (adjustmentMethod === "EXACT_AMOUNT" && targetValue <= totalCost) {
    throw new Error("Target amount must be greater than total cost");
  }

  // Validate margin percentage is reasonable
  if (adjustmentMethod === "MARGIN_PERCENT") {
    if (targetValue < 0) {
      throw new Error("Target margin cannot be negative");
    }
    if (targetValue > 100) {
      throw new Error("Target margin cannot exceed 100%");
    }
  }

  // Business rule warnings for EXACT_AMOUNT
  if (adjustmentMethod === "EXACT_AMOUNT") {
    const calculatedMargin = calculateMarginFromAmount(totalCost, targetValue);
    if (calculatedMargin < 5) {
      warnings.push({
        code: "MARGIN_LOW",
        message: `Calculated margin (${calculatedMargin.toFixed(
          2
        )}%) is below recommended minimum (5%)`,
      });
    }
    if (calculatedMargin > 50) {
      warnings.push({
        code: "MARGIN_HIGH",
        message: `Calculated margin (${calculatedMargin.toFixed(
          2
        )}%) exceeds recommended maximum (50%)`,
      });
    }
  }
  // Business rule warnings for FINAL_AMOUNT (uses current tender amount)
  else if (adjustmentMethod === "FINAL_AMOUNT" && tenderAmount) {
    const calculatedMargin = calculateMarginFromAmount(totalCost, tenderAmount);
    if (calculatedMargin < 5) {
      warnings.push({
        code: "MARGIN_LOW",
        message: `Calculated margin (${calculatedMargin.toFixed(
          2
        )}%) is below recommended minimum (5%)`,
      });
    }
    if (calculatedMargin > 50) {
      warnings.push({
        code: "MARGIN_HIGH",
        message: `Calculated margin (${calculatedMargin.toFixed(
          2
        )}%) exceeds recommended maximum (50%)`,
      });
    }
  }
  // Commented out warnings for MARGIN_PERCENT
  else if (adjustmentMethod === "MARGIN_PERCENT") {
    // if (targetValue < 5) {
    //     warnings.push({
    //         code: 'MARGIN_LOW',
    //         message: `Target margin (${targetValue}%) is below recommended minimum (5%)`
    //     });
    // }
    // if (targetValue > 50) {
    //     warnings.push({
    //         code: 'MARGIN_HIGH',
    //         message: `Target margin (${targetValue}%) exceeds recommended maximum (50%)`
    //     });
    // }
  }

  return { warnings };
};

/**
 * Main function to calculate profit margin adjustments
 * @param {Object} params - Calculation parameters
 * @param {number} params.totalCost - Total project cost
 * @param {number} params.originalTotal - Original tender total
 * @param {number} params.originalMargin - Original profit margin percentage
 * @param {number} params.roundingIncrement - Rounding increment (1000 or 10000)
 * @param {string} params.roundingDirection - 'UP' or 'DOWN'
 * @param {string} params.adjustmentMethod - 'FINAL_AMOUNT' or 'MARGIN_PERCENT'
 * @param {number} params.targetValue - Target amount or margin percentage
 * @returns {Object} Calculation results
 */
export const calculateProfitAdjustment = ({
  // totalCost,
  // originalTotal, -> totalSaleAmount
  // originalMargin, -> totalProfitPercentage
  roundingIncrement,
  roundingDirection,
  adjustmentMethod,
  targetValue,
  projectTotal,
}) => {
  const {
    totalCost,
    totalProfitPercentage,
    totalSaleAmount,
    laborSaleAmount,
    materialCost,
    subcontractorCost,
  } = projectTotal;

  const originalTenderTotal = roundValue(toDecimal(totalSaleAmount), 2);
  const originalProfitMargin = roundValue(toDecimal(totalProfitPercentage), 4);

  // cost to be adjusted (materials + subcontractors)
  const adjustableCost = roundValue(
    toDecimal(materialCost).plus(toDecimal(subcontractorCost)),
    2
  );

  let adjustedTenderTotal,
    adjustedMargin,
    adjustedTotalCost,
    adjustedTotalProfit,
    adjustedProfitMargin;
  let notes = {};
  let validation = { warnings: [] }; // Initialize validation with empty warnings

  if (adjustmentMethod === "FINAL_AMOUNT") {
    // Method 1: Rounding Method - Round CURRENT tender total
    // User does NOT enter a target amount - we round the existing tender total

    // Validate inputs (no targetValue needed for FINAL_AMOUNT)
    validation = validateInputs({
      totalCost,
      adjustmentMethod,
      tenderAmount: totalSaleAmount,
    });

    // Step 1: Round the CURRENT tender total
    adjustedTenderTotal = roundToIncrement(
      totalSaleAmount,
      roundingIncrement,
      roundingDirection
    );

    // Step 2: Calculate the overall profit margin after rounding
    adjustedProfitMargin = calculateMarginFromAmount(
      totalCost,
      adjustedTenderTotal
    );

    // Step 3: Calculate the adjustable portion (excluding fixed labor) for internal calculations
    const adjustableSaleAmount = roundValue(
      toDecimal(adjustedTenderTotal).minus(toDecimal(laborSaleAmount)),
      2
    );
    adjustedMargin = calculateMarginFromAmount(
      adjustableCost,
      adjustableSaleAmount
    );

    // Step 4: Calculate total profit
    adjustedTotalProfit = roundValue(
      toDecimal(adjustedTenderTotal).minus(toDecimal(totalCost)),
      2
    );

    // Notes
    notes = {
      totalCost: "Cost unchanged",
      profitMargin: `Original: ${originalProfitMargin.toFixed(
        2
      )}% → After rounding: ${roundValue(adjustedProfitMargin, 2).toFixed(2)}%`,
      tenderTotal: `Original: ${toUSD(
        roundValue(totalSaleAmount, 2)
      )} → Rounded: ${toUSD(roundValue(adjustedTenderTotal, 2))}`,
      difference:
        roundValue(adjustedTenderTotal, 2) > originalTenderTotal
          ? `+${toUSD(
              roundValue(
                toDecimal(adjustedTenderTotal).minus(
                  toDecimal(originalTenderTotal)
                ),
                2
              )
            )}`
          : toUSD(
              roundValue(
                toDecimal(adjustedTenderTotal).minus(
                  toDecimal(originalTenderTotal)
                ),
                2
              )
            ),
      totalProfit: "Tender Total - Total Cost",
    };
  } else if (adjustmentMethod === "EXACT_AMOUNT") {
    // Method 2: Exact Target Amount (no rounding)
    // User specifies exact target tender amount, calculate the margin needed

    // Validate inputs
    validation = validateInputs({ totalCost, targetValue, adjustmentMethod });

    const targetAmount = roundValue(toDecimal(targetValue), 2);

    // Step 1: Use exact target amount (no rounding)
    adjustedTenderTotal = targetAmount;

    // Step 2: Calculate the overall profit margin
    adjustedProfitMargin = calculateMarginFromAmount(
      totalCost,
      adjustedTenderTotal
    );

    // Step 3: Calculate the adjustable portion (excluding fixed labor) for internal calculations
    const adjustableSaleAmount = adjustedTenderTotal - laborSaleAmount;
    adjustedMargin = calculateMarginFromAmount(
      adjustableCost,
      adjustableSaleAmount
    );

    // Step 4: Calculate total profit
    adjustedTotalProfit = adjustedTenderTotal - totalCost;

    // Notes
    notes = {
      totalCost: "Cost unchanged",
      profitMargin: `Exact target: ${roundValue(
        adjustedProfitMargin,
        2
      ).toFixed(2)}%`,
      tenderTotal: `Exact amount: ${toUSD(roundValue(adjustedTenderTotal, 2))}`,
      difference:
        roundValue(adjustedTenderTotal, 2) > originalTenderTotal
          ? `+${toUSD(
              roundValue(
                toDecimal(adjustedTenderTotal).minus(
                  toDecimal(originalTenderTotal)
                ),
                2
              )
            )}`
          : toUSD(
              roundValue(
                toDecimal(adjustedTenderTotal).minus(
                  toDecimal(originalTenderTotal)
                ),
                2
              )
            ),
      totalProfit: "Tender Total - Total Cost",
    };
  } else if (adjustmentMethod === "MARGIN_PERCENT") {
    // Method 3: Margin Percent Method - Calculate tender from margin, then round
    // User specifies target margin percentage

    // Validate inputs
    validation = validateInputs({ totalCost, targetValue, adjustmentMethod });

    // Method 2: Adjust by Margin % (OVERALL target; labor fixed)
    const targetMargin = roundValue(toDecimal(targetValue), 4); // e.g., 35 => 35%

    // 1) Overall tender needed to hit target (pre-round)
    const requiredTenderTotal = calculateAmountFromMargin(
      totalCost,
      targetMargin
    );

    // 2) Apply rounding to overall tender
    adjustedTenderTotal = roundToIncrement(
      requiredTenderTotal,
      roundingIncrement,
      roundingDirection
    );

    // 3) Back-solve adjustable sale given fixed labor sale
    const finalAdjustableSaleAmount = roundValue(
      toDecimal(adjustedTenderTotal).minus(toDecimal(laborSaleAmount)),
      2
    );

    // 4) Recompute margins for preview
    adjustedTotalProfit = roundValue(
      toDecimal(adjustedTenderTotal).minus(toDecimal(totalCost)),
      2
    ); // $
    adjustedProfitMargin = calculateMarginFromAmount(
      totalCost,
      adjustedTenderTotal
    ); // overall %
    adjustedMargin =
      adjustableCost > 0
        ? calculateMarginFromAmount(adjustableCost, finalAdjustableSaleAmount) // effective mats+subs %
        : 0;

    // 5) Notes
    notes = {
      totalCost: "Labor unchanged; only materials & subcontractors scaled",
      profitMargin: `Target=${targetMargin.toFixed(
        2
      )}% → After rounding=${roundValue(adjustedProfitMargin, 2)}%`,
      tenderTotal: "Rounded to increment",
      difference:
        roundValue(adjustedTenderTotal, 2) > originalTenderTotal
          ? `+${toUSD(
              roundValue(
                toDecimal(adjustedTenderTotal).minus(
                  toDecimal(originalTenderTotal)
                ),
                2
              )
            )}`
          : toUSD(
              roundValue(
                toDecimal(adjustedTenderTotal).minus(
                  toDecimal(originalTenderTotal)
                ),
                2
              )
            ),
      totalProfit: "Tender Total - Total Cost",
    };
  }

  // Round all values for consistency
  adjustedTenderTotal = roundValue(adjustedTenderTotal, 2);
  adjustedMargin = roundValue(adjustedMargin, 2);
  adjustedProfitMargin = roundValue(adjustedProfitMargin, 2);
  adjustedTotalProfit = roundValue(adjustedTotalProfit, 2);
  adjustedTotalCost = roundValue(totalCost, 2);

  const result = {
    adjustedTenderTotal,
    adjustedMargin,
    adjustedProfitMargin,
    adjustableMargin: adjustedMargin,
    adjustedTotalCost,
    adjustedTotalProfit,
    difference: adjustedTenderTotal - originalTenderTotal,
    notes,
    warnings: validation.warnings,
  };

  return result;
};
